// Batch C (AB#2195) — Super Admin support tools: cross-salon booking search,
// a salon's open bookings, admin cancel, salon suspend / reinstate. Reads are
// TanStack queries; every write invalidates all admin booking views (the
// Owners list, which is not a query, reloads through its onChanged callback).

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    apiService,
    type AdminSupportAppointmentListResponse,
    type AdminSupportAppointmentDto,
    type SearchAdminAppointmentsParams,
} from '../services/api';
import { queryKeys } from '../lib/queryKeys';

/** Search is disabled until a booking number or phone is given. */
export function useAdminBookingSearch(params: SearchAdminAppointmentsParams | null) {
    return useQuery<AdminSupportAppointmentListResponse>({
        queryKey: queryKeys.adminBookings.search((params ?? {}) as Record<string, unknown>),
        queryFn: () => apiService.searchAdminAppointments(params as SearchAdminAppointmentsParams),
        enabled: params != null,
        staleTime: 0,
        retry: false,
        // Keep the current page on screen while the next one loads.
        placeholderData: keepPreviousData,
    });
}

export function useAdminOpenBookings(businessId: number | null) {
    return useQuery<AdminSupportAppointmentDto[]>({
        queryKey: queryKeys.adminBookings.open(businessId ?? 0),
        queryFn: () => apiService.getOpenBookingsForAdmin(businessId as number),
        enabled: businessId != null,
        staleTime: 0,
    });
}

export function useAdminCancelBooking() {
    const client = useQueryClient();
    return useMutation({
        mutationFn: ({ appointmentId, reason }: { appointmentId: number; reason: string }) =>
            apiService.cancelAppointmentAsAdmin(appointmentId, reason),
        onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.adminBookings.all }),
    });
}

export function useAdminSalonStatus() {
    const client = useQueryClient();
    const invalidate = () => client.invalidateQueries({ queryKey: queryKeys.adminBookings.all });
    const suspend = useMutation({
        mutationFn: ({ businessId, reason }: { businessId: number; reason: string }) =>
            apiService.suspendBusiness(businessId, reason),
        onSuccess: invalidate,
    });
    const reinstate = useMutation({
        mutationFn: ({ businessId, reason }: { businessId: number; reason?: string }) =>
            apiService.reinstateBusiness(businessId, reason),
        onSuccess: invalidate,
    });
    return { suspend, reinstate };
}
